import {ButtonDropdown, DropdownList, EMPHASIS, FlexColumn, FlexRow, Modal, Text} from '@forecast-it/design-system';
import React, {useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../shared/components/modals/types';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import Util from '../../../shared/util/util';
import DeleteProjectStageMutation from '../../../../mutations/DeleteProjectStageMutation';
import type {StageProfile} from '../../../shared/util/projectStageUtil';
import {
	isStageHasProjects,
	isStageLastStage,
	isStageLastWithProfile,
	isStageMoveTargetInvalid,
	isStageOpportunityNotAvailable,
} from '../projectStageValidation';
import type {DeleteProjectStageModalCountQuery$data} from './__generated__/DeleteProjectStageModalCountQuery.graphql';

export type DeleteProjectStageModalPayload = {
	/** The company's Relay id; the stage is removed from its projectStages connection. */
	companyId: string;
	/** The stage, with the label the page shows for it. */
	stage: {id: string; label: string; profile: StageProfile | null};
	/** The company's other stages in admin order: where the stage's projects can move. */
	otherStages: Array<{id: string; label: string; profile: StageProfile | null}>;
	/** The stage's project count as the page loaded it. */
	projectCount: number;
	/** Whether no other stage has this stage's profile, as the page saw it. */
	lastWithProfile: boolean;
	/** Whether it is the company's only stage, as the page saw it. Its warning takes precedence. */
	lastStage: boolean;
};

export const showDeleteProjectStageModal = (props: DeleteProjectStageModalPayload): void => {
	showModal({
		type: MODAL_TYPE.DELETE_PROJECT_STAGE,
		...props,
	});
};

// Re-read in two cases: when the dialog opens blocked (the stage's projects may have moved out since the page
// loaded) and when the server refuses the delete (projects moved in). Fetching into the store also updates the
// page's rows.
const COUNT_QUERY = graphql`
	query DeleteProjectStageModalCountQuery {
		viewer {
			company {
				projectStages(first: 1000) {
					edges {
						node {
							id
							projectCount
						}
					}
				}
			}
		}
	}
`;

/**
 * Deletes a stage. A stage with projects moves them to another stage the admin picks first, or stays blocked when
 * there is none. Warns first when it is the company's last stage or, otherwise, the last stage with its profile.
 */
export const DeleteProjectStageModal = ({
	closeModal,
	companyId,
	stage,
	otherStages,
	projectCount,
	lastWithProfile: lastWithProfileOnOpen,
	lastStage: lastStageOnOpen,
}: ModalProps<DeleteProjectStageModalPayload>): JSX.Element => {
	const intl = useIntl();
	const [count, setCount] = useState(projectCount);
	const [deleting, setDeleting] = useState(false);
	const [lastWithProfile, setLastWithProfile] = useState(lastWithProfileOnOpen);
	const [lastStage, setLastStage] = useState(lastStageOnOpen);
	const [target, setTarget] = useState<string | null>(null);
	// The target of the delete in flight, so a refusal drops the stage that was actually sent.
	const sentTarget = useRef<string | null>(null);
	const [goneTargets, setGoneTargets] = useState<string[]>([]);
	// Why the server refused the picked stage, shown until the admin picks another.
	const [targetRefused, setTargetRefused] = useState<'gone' | 'opportunity' | null>(null);
	// Set once the server refuses an Opportunity stage: the access the page loaded with is out of date.
	const [opportunityRefused, setOpportunityRefused] = useState(false);
	const {fetch} = useForecastFetchQuery<DeleteProjectStageModalCountQuery$data, Record<string, never>>(COUNT_QUERY);

	// A delete outlives the modal if it is dismissed mid-flight; a late answer must not touch a closed modal.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Shows the stage's count as the server has it now: the move or blocked state while projects are in it, the
	// confirm state once none are (they may have moved out since the page loaded or since a refused delete).
	const applyFreshCount = (data: DeleteProjectStageModalCountQuery$data) => {
		const node = data.viewer?.company?.projectStages?.edges
			?.map(edge => edge?.node)
			.find(candidate => candidate?.id === stage.id);
		if (mounted.current) {
			setCount(node?.projectCount ?? 0);
			setDeleting(false);
		}
	};

	// The page's count may be out of date. A dialog that opens with projects in the stage (move or blocked) re-checks it,
	// so projects moved out since the page loaded don't keep it there. If the re-check fails the page's count stays: it
	// is still the server's answer.
	useEffect(() => {
		if (projectCount > 0) {
			fetch({}, applyFreshCount);
		}
		// Only on open: `fetch` is a new function on every render.
	}, []);

	const profileName = (profile: StageProfile | null) =>
		profile ? intl.formatMessage({id: `project_status.${profile.toLowerCase()}`}) : '';
	const targets = otherStages.filter(
		candidate =>
			!goneTargets.includes(candidate.id) &&
			(candidate.profile !== 'OPPORTUNITY' || (!opportunityRefused && Util.hasOpportunityAccess()))
	);
	const picked = targets.find(candidate => candidate.id === target) ?? null;

	const handleError = (error: Error, onFailure: (error: Error) => boolean | void) => {
		const showDefaultError = () => {
			if (onFailure(error) !== true && mounted.current) {
				setDeleting(false);
			}
		};
		if (isStageHasProjects(error)) {
			// If the admin closed the dialog the stage simply stays. Otherwise show the fresh count; if it can't be
			// read, report the refusal through the default error handling rather than guess a count.
			if (mounted.current) {
				fetch({}, applyFreshCount, showDefaultError);
			}
			return;
		}
		if (isStageLastStage(error)) {
			// The other stages went away since the page loaded: warn that it is the last one, then let the admin go ahead.
			if (mounted.current) {
				setLastStage(true);
				setDeleting(false);
			}
			return;
		}
		if (isStageLastWithProfile(error)) {
			// Another stage with this profile went away since the page loaded: warn, then let the admin go ahead.
			if (mounted.current) {
				setLastWithProfile(true);
				setDeleting(false);
			}
			return;
		}
		if (isStageMoveTargetInvalid(error)) {
			// The picked stage was deleted since the page loaded: drop it and ask for another.
			if (mounted.current) {
				const sent = sentTarget.current;
				setGoneTargets(gone => (sent ? [...gone, sent] : gone));
				setTarget(null);
				setTargetRefused('gone');
				setDeleting(false);
			}
			return;
		}
		if (isStageOpportunityNotAvailable(error)) {
			// Opportunity access went away since the page loaded: drop every Opportunity stage and ask for another.
			if (mounted.current) {
				setOpportunityRefused(true);
				setTarget(null);
				setTargetRefused('opportunity');
				setDeleting(false);
			}
			return;
		}
		showDefaultError();
	};

	const handleDeleted = () => {
		if (sentTarget.current) {
			// The target's count changed; re-reading the counts updates the page's rows through the store.
			fetch({}, () => undefined);
		}
		if (mounted.current) {
			closeModal();
		}
	};

	const hasProjects = count > 0;
	const moving = hasProjects && targets.length > 0;
	const blocked = hasProjects && !moving;
	const warnLastStage = !hasProjects && lastStage;
	const warnProfile = (!hasProjects || moving) && !lastStage && lastWithProfile && !!stage.profile;
	const warn = warnLastStage || warnProfile;

	const handleDelete = () => {
		if (deleting || (moving && !picked)) {
			return;
		}
		setDeleting(true);
		sentTarget.current = moving && picked ? picked.id : null;
		// Not retried: a retry after a delete that went through would answer that the stage is gone. The go-ahead
		// is sent only when the admin was shown one of the two warnings; it covers both.
		Util.CommitMutation(
			DeleteProjectStageMutation,
			{
				companyId,
				id: stage.id,
				allowLastWithProfile: warn,
				...(sentTarget.current ? {moveProjectsTo: sentTarget.current} : {}),
			},
			handleDeleted,
			false,
			null,
			handleError
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>
				{intl.formatMessage({
					id: moving
						? 'settings.project_stages.move.title'
						: blocked
						? 'settings.project_stages.delete.blocked_title'
						: 'settings.project_stages.delete',
				})}
			</Modal.Title>
			<Modal.Content>
				<FlexColumn>
					<div data-cy="project-stage-delete-message">
						{moving
							? intl.formatMessage({id: 'settings.project_stages.move.body'}, {count, name: stage.label})
							: blocked
							? intl.formatMessage({id: 'settings.project_stages.delete.blocked'}, {count, name: stage.label})
							: warnLastStage
							? intl.formatMessage({id: 'settings.project_stages.last_stage'}, {name: stage.label})
							: warnProfile
							? intl.formatMessage(
									{id: 'settings.project_stages.last_with_profile'},
									{name: stage.label, profile: profileName(stage.profile)}
							  )
							: intl.formatMessage({id: 'settings.project_stages.delete.confirm'}, {name: stage.label})}
					</div>
					{moving ? (
						<FlexRow>
							<Text>{intl.formatMessage({id: 'settings.project_stages.move.label'})}</Text>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdown
										emphasis={EMPHASIS.LOW}
										disabled={deleting}
										aria-label={`${intl.formatMessage({id: 'settings.project_stages.move.label'})} ${
											picked
												? picked.label
												: intl.formatMessage({id: 'settings.project_stages.move.placeholder'})
										}`}
										data-cy="project-stage-move-target"
									>
										{picked
											? picked.label
											: intl.formatMessage({id: 'settings.project_stages.move.placeholder'})}
									</ButtonDropdown>
								</DropdownList.Trigger>
								<DropdownList.Content offset={4} align={'start'}>
									{targets.map(candidate => (
										<DropdownList.Item
											key={candidate.id}
											onClick={() => {
												setTarget(candidate.id);
												setTargetRefused(null);
											}}
											data-cy={`project-stage-move-target-${candidate.id}`}
										>
											{intl.formatMessage(
												{id: 'settings.project_stages.move.option'},
												{name: candidate.label, profile: profileName(candidate.profile)}
											)}
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						</FlexRow>
					) : null}
					{moving && targetRefused === 'gone' ? (
						<div role="alert" data-cy="project-stage-move-target-gone">
							<Text>{intl.formatMessage({id: 'settings.project_stages.move.target_gone'})}</Text>
						</div>
					) : null}
					{moving && targetRefused === 'opportunity' ? (
						<div role="alert" data-cy="project-stage-move-opportunity-unavailable">
							<Text>{intl.formatMessage({id: 'settings.project_stages.move.opportunity_unavailable'})}</Text>
						</div>
					) : null}
					{moving && warnProfile ? (
						<div role="alert" data-cy="project-stage-move-warning">
							<Text>
								{intl.formatMessage(
									{id: 'settings.project_stages.last_with_profile'},
									{name: stage.label, profile: profileName(stage.profile)}
								)}
							</Text>
						</div>
					) : null}
				</FlexColumn>
				<FlexRow>
					{blocked ? (
						<Modal.SecondaryFooterButton onPress={closeModal} dataCy="project-stage-delete-close">
							{intl.formatMessage({id: 'common.close'})}
						</Modal.SecondaryFooterButton>
					) : (
						<>
							<Modal.PrimaryFooterButton
								onPress={handleDelete}
								disabled={deleting || (moving && !picked)}
								dataCy="project-stage-delete-confirm"
							>
								{intl.formatMessage({
									id: moving
										? warn
											? 'settings.project_stages.move.confirm_anyway'
											: 'settings.project_stages.move.confirm'
										: warn
										? 'settings.project_stages.delete.anyway'
										: 'common.delete',
								})}
							</Modal.PrimaryFooterButton>
							<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
								{intl.formatMessage({id: 'common.cancel'})}
							</Modal.SecondaryFooterButton>
						</>
					)}
				</FlexRow>
			</Modal.Content>
		</Modal>
	);
};
